import { fetchContacts, fetchZAttributes } from "../../../api";
import { AttrType } from "../../../types";

// MBAI-445: shared resolver so every contact display (pickers, lists, detail views)
// consistently prefers the business name over the individual contact name when the
// contact is marked as a business.
export const getContactDisplayName = (
  contact?: { isBusiness?: boolean; companyName?: string; contactName?: string } | null
): string => {
  if (!contact) return '';
  if (contact.isBusiness && contact.companyName) {
    return contact.companyName;
  }
  return contact.contactName || '';
};

export const addContactAttributesToList = async (list: any[]) => {
    try {
        // Fetch the UOMs, contact categories, and contact groups

        const [contacts, contactCategories, contactGroups] = await Promise.all([
            fetchContacts(),
            fetchZAttributes(AttrType.ContactCategory),
            fetchZAttributes(AttrType.ContactGroup),
        ]);

        // Ensure we have valid arrays
        const contactsArray = Array.isArray(contacts) ? contacts : [];
        const contactCategoriesArray = Array.isArray(contactCategories) ? contactCategories : [];
        const contactGroupsArray = Array.isArray(contactGroups) ? contactGroups : [];

        // Create lookup maps for uoms, contact categories, and contact groups
        const contactMap = new Map(contactsArray.map(contact => [contact.contactId, contact]));
        const contactCategoryMap = new Map(contactCategoriesArray.map(category => [category.attrId, category]));
        const contactGroupMap = new Map(contactGroupsArray.map(group => [group.attrId, group]));

        // Iterate through the list and add attributes if the contact has an contactId
        const listWithAttributes = list.map(listContact => {
            // Get contactId from either direct property or nested structure
            const contactId = listContact.contactId || 
                            listContact.txnHeader?.contactId ||
                            listContact.paymentHeader?.contactId;

            if (contactId) {
                const contact = contactMap.get(contactId);
                if (contact) {
                    const contactCategory = contactCategoryMap.get(contact.contactCategory || '');
                    const contactGroup = contactGroupMap.get(contact.contactGroup || '');

                    return {
                        ...listContact,
                        ...contact,
                        ...{
                            contactCategory: contactCategory?.attrNm,
                            contactCategoryColor: contactCategory?.attrColor
                        },
                        ...(contactGroup || {}),
                    };
                }
            }

            
            return listContact; // Return unchanged if no contactId found
        });

        return listWithAttributes;
    } catch (error) {
        console.error('Error fetching data:', error);
        return list; // Return the original list if there was an error
    }
};

export const addContactAttributesToSingleContact = async (contactId: string) => {
    try {
        // Fetch the contacts, contact categories, and contact groups
        const [contacts, contactCategories, contactGroups] = await Promise.all([
            fetchContacts(),
            fetchZAttributes(AttrType.ContactCategory),
            fetchZAttributes(AttrType.ContactGroup),
        ]);

        // Ensure we have valid arrays
        const contactsArray = Array.isArray(contacts) ? contacts : [];
        const contactCategoriesArray = Array.isArray(contactCategories) ? contactCategories : [];
        const contactGroupsArray = Array.isArray(contactGroups) ? contactGroups : [];

        // Create lookup maps for contacts, contact categories, and contact groups
        const contactMap = new Map(contactsArray.map(contact => [contact.contactId, contact]));
        const contactCategoryMap = new Map(contactCategoriesArray.map(category => [category.attrId, category]));
        const contactGroupMap = new Map(contactGroupsArray.map(group => [group.attrId, group]));

        // Find the contact based on contactId
        const contact = contactMap.get(contactId);
        if (!contact) {
          return
            // throw new Error(`Contact with ID ${contactId} not found.`);
        }

        // Add contact category and group attributes
        const contactCategory = contactCategoryMap.get(contact.contactCategory || '');
        const contactGroup = contactGroupMap.get(contact.contactGroup || '');

        // Return the contact with added attributes
        return {
            ...contact,
            contactCategory: contactCategory?.attrNm,
            contactCategoryColor: contactCategory?.attrColor,
            ...(contactGroup || {}),
        };
    } catch (error) {
        console.error('Error fetching data:', error);
        return null; // Return null if there was an error
    }
};

import React, { useEffect, useState } from 'react';

interface Contact {
    contactId: string;
    contactName?: string; // Mark as optional
    isBusiness?: boolean;
    companyName?: string;
  }
  

interface TxnHeader {
  contactId: string;
  contactName: string;
}

interface Row {
  original: {
    txnHeader: TxnHeader;
  };
}

interface ContactDisplayProps {
  contactId: string;
  contactName?: string;
}


export const ContactDisplay: React.FC<ContactDisplayProps> = ({ contactId,contactName }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);

  useEffect(() => {
    const getContacts = async () => {
      const contactResult = await fetchContacts();
      setContacts(contactResult);
    };
    
    getContacts();
  }, []);

  const contactDetail = Array.isArray(contacts)
    ? contacts.find(contact => contact.contactId === contactId )
    : null;

  return (
    <div>
      {getContactDisplayName(contactDetail) || contactName}
    </div>
  );
};


